/* eslint-disable @typescript-eslint/no-use-before-define */
import { Typography } from '@euler/app/components/typography';
import {
  ObdDeviceModal,
  ObdDeviceModalRef,
} from '@euler/app/flows/settings/components/ObdDeviceModal';
import { useObdDevices } from '@euler/app/flows/settings/functions';
import { SettingsNavParams } from '@euler/app/flows/settings/screens';
import { EmptyView } from '@euler/components/EmptyView';
import { Icon } from '@euler/components/Icomoon';
import { HStack } from '@euler/components/layout/Stack';
import { useAppLoading } from '@euler/components/loading/Provider';
import { PredefinedHitSlops } from '@euler/functions/predefinedHitSlops';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { useTask } from '@euler/functions/useTask';
import { wrapNavigatorScreen } from '@euler/functions/wrapNavigatorScreen';
import { useToggleAutomaticKeyboard } from '@euler/lib/keyboard';
import { delayed, showAlert, withConfirmation } from '@euler/utils';
import { formatDateTime } from '@euler/utils/formatters';
import { usePersistFn } from '@euler/utils/hooks';
import { useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import {
  ReactNode,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Animated,
  FlatList,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';
import { RectButton } from 'react-native-gesture-handler';
import Swipeable from 'react-native-gesture-handler/Swipeable';

export const ObdDeviceScreen = wrapNavigatorScreen(
  () => {
    useToggleAutomaticKeyboard(true);

    const { rpx } = useResponsiveContext();
    const navigation = useNavigation<StackNavigationProp<SettingsNavParams>>();

    const { devices, createAsync, updateAsync, deleteAsync } = useObdDevices();

    const ref = useRef<ObdDeviceModalRef>(null);
    const appLoading = useAppLoading();
    const [showModal, setShowModal] = useState(false);
    const [title, setTitle] = useState('');
    const [deviceIdBeingEdited, setDeviceIdBeingEdited] = useState<string>();

    const onAdd = usePersistFn(() => {
      setTitle('添加OBD设备');
      setShowModal(true);
    });

    const onEdit = usePersistFn((id: string) => {
      setTitle('修改OBD设备');
      setShowModal(true);
      setDeviceIdBeingEdited(id);
      const device = devices.find(x => x.id == id)!;
      delayed(() => {
        ref.current?.controller?.form.serialNo.update(device.serialNo);
        ref.current?.controller?.form.name.update(device.name);
      }, 100);
    });

    const onDelete = usePersistFn(async (id: string) => {
      const confirmed = await withConfirmation({
        title: '确认',
        message: '您即将删除该设备，该操作不可撤销，是否继续?',
        confirmButtonText: '是',
        cancelButtonText: '否',
      });
      if (confirmed) {
        try {
          await appLoading.showWithAction(async () => {
            await deleteAsync(id);
          });
        } catch (e) {
          void showAlert('出错了', (e as Error).message);
        }
      }
    });

    const onDismiss = useCallback(() => {
      ref.current?.controller?.form.serialNo.update('');
      ref.current?.controller?.form.name.update('');
      setShowModal(false);
    }, []);

    useEffect(() => {
      navigation.setOptions({
        headerRight: () => (
          <TouchableOpacity
            hitSlop={PredefinedHitSlops.large}
            onPress={onAdd}
            css={`
              margin-right: ${rpx(15)}px;
            `}
          >
            <Icon name="plus-circle" color="white" size={rpx(22)} />
          </TouchableOpacity>
        ),
      });
    }, [navigation, onAdd, rpx]);

    const onSave = useTask(async () => {
      const controller = ref.current?.controller;
      if (controller == null) return;
      const valid = await controller.validateAll();
      if (!valid) return;
      const serialNo = controller.form.serialNo.value;
      const name = controller.form.name.value;

      try {
        await appLoading.showWithAction(async () => {
          if (deviceIdBeingEdited == null) {
            await createAsync({ serialNo, name });
          } else {
            await updateAsync(deviceIdBeingEdited, { serialNo, name });
          }
          setShowModal(false);
        });
      } catch (e) {
        void showAlert('出错了', (e as Error).message);
      }
    });

    return (
      <>
        <FlatList
          data={devices}
          keyExtractor={x => x.id}
          contentContainerStyle={{ flexGrow: 1, paddingTop: rpx(15) }}
          renderItem={({ item, index }) => (
            <Cell
              id={item.id}
              name={item.name}
              serialNo={item.serialNo}
              timestamp={item.createdAt}
              first={index === 0}
              last={index === devices.length - 1}
              onEdit={onEdit}
              onDelete={onDelete}
            />
          )}
          ListFooterComponent={
            devices.length === 0 ? (
              <EmptyView message="门店暂无OBD相关设备" />
            ) : undefined
          }
          ListFooterComponentStyle={{ flex: 1 }}
          css={`
            flex: 1;
          `}
        />
        <ObdDeviceModal
          ref={ref}
          visible={showModal}
          title={title}
          onDismiss={onDismiss}
          onSave={onSave.request}
          loading={onSave.loading}
        />
      </>
    );
  },
  {
    title: 'OBD设备管理',
  },
);

const Cell = memo(
  ({
    id,
    first,
    last,
    name,
    serialNo,
    timestamp,
    onEdit,
    onDelete,
  }: {
    id: string;
    name: string;
    serialNo: string;
    timestamp: string | Date;
    first?: boolean;
    last?: boolean;
    onEdit?: (id: string) => void;
    onDelete?: (id: string) => void;
  }) => {
    const { rpx } = useResponsiveContext();
    const styles = useStyles();

    const ref = useRef<Swipeable>(null);

    const renderRightAction = usePersistFn(
      (
        progress: Animated.AnimatedInterpolation<number>,
        text: string,
        color: string,
        x: number,
        onPress?: () => void,
      ): ReactNode => {
        const trans = progress.interpolate({
          inputRange: [0, 1],
          outputRange: [x, 0],
        });
        return (
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          <Animated.View
            style={{ flex: 1, transform: [{ translateX: trans }] }}
          >
            <RectButton
              style={[styles.rightAction, { backgroundColor: color }]}
              onPress={onPress}
            >
              <Text style={styles.actionText}>{text}</Text>
            </RectButton>
          </Animated.View>
        );
      },
    );

    const renderRightActions = usePersistFn(
      (progress: Animated.AnimatedInterpolation<number>) => {
        return (
          <View
            css={`
              flex-direction: row;
              width: ${rpx(128)}px;
            `}
          >
            {renderRightAction(progress, '修改', '#C8C7CD', rpx(128), () => {
              ref.current?.close();
              onEdit?.(id);
            })}
            {renderRightAction(progress, '删除', '#dd2c00', rpx(64), () => {
              ref.current?.close();
              onDelete?.(id);
            })}
          </View>
        );
      },
    );

    // https://docs.swmansion.com/react-native-gesture-handler/docs/api/components/swipeable
    return (
      <Swipeable
        ref={ref}
        renderRightActions={renderRightActions}
        overshootRight={false}
        containerStyle={{
          backgroundColor: '#fff',
        }}
      >
        <HStack
          justifyContent="flex-start"
          css={`
            padding: ${rpx(15)}px;
          `}
        >
          <Icon name="devices" size={rpx(44)} color={'#999'} />
          <View
            css={`
              margin-left: ${rpx(15)}px;
            `}
          >
            <Typography.Label regular size={rpx(20)}>
              {name}
            </Typography.Label>
            <Typography.Label
              light
              size={rpx(14)}
              css={`
                margin-top: ${rpx(4)}px;
              `}
            >
              {`注册时间: ${formatDateTime(timestamp)}`}
            </Typography.Label>
            <Typography.Label
              size={rpx(14)}
              color="#444"
              light
              css={`
                margin-top: ${rpx(4)}px;
              `}
            >
              {`设备序列号: ${serialNo}`}
            </Typography.Label>
          </View>
        </HStack>
        {first && (
          <View
            css={`
              position: absolute;
              left: 0;
              top: 0;
              right: 0;
              height: 1px;
              background-color: #eee;
            `}
          />
        )}
        <View
          css={`
            position: absolute;
            left: ${last ? 0 : rpx(70)}px;
            bottom: 0;
            right: 0;
            height: 1px;
            background-color: #eee;
          `}
        />
      </Swipeable>
    );
  },
);

const useStyles = () => {
  const { rpx } = useResponsiveContext();
  return useMemo(
    () =>
      StyleSheet.create({
        leftAction: {
          flex: 1,
          backgroundColor: '#497AFC',
          justifyContent: 'center',
        },
        actionText: {
          color: 'white',
          fontSize: rpx(16),
          backgroundColor: 'transparent',
          padding: rpx(10),
        },
        rightAction: {
          alignItems: 'center',
          flex: 1,
          justifyContent: 'center',
        },
      }),
    [rpx],
  );
};
